import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Phone, MapPin, Clock, Store, Truck, CreditCard, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCESS_CODE = "13011"; // code d'accès simple pour le restaurant
const fmt = (n) => Number(n || 0).toFixed(2).replace(".", ",") + "€";

const timeAgo = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const OrderLine = ({ l }) => {
  const bits = [];
  bits.push(l.format === "menu" ? "Menu" : "Seul");
  if (l.meats?.length) bits.push(l.meats.join(", "));
  if (l.sauces?.length) bits.push("Sauces: " + l.sauces.join(", "));
  if (l.boisson) bits.push(l.boisson);
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-white/5 last:border-0">
      <div className="text-sm">
        <span className="text-white font-semibold">{l.qty}× {l.name}</span>
        <span className="block text-white/45 text-xs">{bits.join(" · ")}</span>
      </div>
      <span className="text-gold text-sm whitespace-nowrap">{fmt(l.unitPrice * l.qty)}</span>
    </div>
  );
};

const Commandes = () => {
  const [authed, setAuthed] = useState(() => localStorage.getItem("mp_admin_ok") === "1");
  const [code, setCode] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/orders`);
      setOrders(Array.isArray(data) ? data : []);
      setLastUpdate(new Date());
      setErr("");
    } catch (e) {
      setErr("Impossible de charger les commandes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    load();
    const t = setInterval(load, 15000); // rafraîchit toutes les 15s
    return () => clearInterval(t);
  }, [authed, load]);

  if (!authed) {
    return (
      <div className="brick-bg min-h-screen flex items-center justify-center px-4 pt-24">
        <div className="bg-[#141414] border border-white/10 rounded-3xl p-8 max-w-sm w-full text-center">
          <Lock className="text-pink mx-auto mb-3" size={36} />
          <h1 className="font-display text-2xl uppercase text-white">Espace Restaurant</h1>
          <p className="text-white/50 text-sm mt-1 mb-5">Entrez le code d'accès pour voir les commandes.</p>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && code === ACCESS_CODE) { localStorage.setItem("mp_admin_ok", "1"); setAuthed(true); } }}
            placeholder="Code"
            className="w-full bg-[#181818] border border-white/15 rounded-xl px-4 py-3 text-white text-center focus:border-pink outline-none"
          />
          <button
            onClick={() => { if (code === ACCESS_CODE) { localStorage.setItem("mp_admin_ok", "1"); setAuthed(true); } else { setErr("Code incorrect"); } }}
            className="w-full mt-4 bg-pink text-white font-display uppercase py-3 rounded-full btn-shine"
          >
            Entrer
          </button>
          {err && <p className="text-red-400 text-sm mt-3">{err}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="brick-bg min-h-screen pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <span className="font-script text-3xl text-pink">Espace resto</span>
            <h1 className="font-display text-3xl md:text-4xl uppercase text-white">Commandes reçues</h1>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdate && <span className="text-white/40 text-xs">MàJ {lastUpdate.toLocaleTimeString("fr-FR")}</span>}
            <button onClick={load} className="flex items-center gap-2 bg-pink text-white px-4 py-2 rounded-full btn-shine font-semibold text-sm">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Actualiser
            </button>
          </div>
        </div>

        <div className="mb-5 text-white/60 text-sm">
          <span className="text-gold font-bold text-lg">{orders.length}</span> commande(s) · rafraîchissement auto toutes les 15s
        </div>

        {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-3 mb-4 text-sm">{err}</div>}

        {orders.length === 0 ? (
          <div className="text-center py-20 text-white/40">Aucune commande pour le moment.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {orders.map((o) => (
              <div key={o.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-display text-xl text-gold">{o.ref}</span>
                  <span className="flex items-center gap-1 text-white/40 text-xs"><Clock size={13} /> {timeAgo(o.created_at)}</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3 text-xs">
                  <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full ${o.mode === "livraison" ? "bg-pink/15 text-pink" : "bg-gold/15 text-gold"}`}>
                    {o.mode === "livraison" ? <Truck size={12} /> : <Store size={12} />} {o.mode === "livraison" ? "Livraison" : "À emporter"}
                  </span>
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 text-white/80">
                    <CreditCard size={12} /> {o.paymentMethod}{o.paymentDetail ? ` (${o.paymentDetail})` : ""}
                  </span>
                </div>
                <div className="text-sm text-white/80 mb-2">
                  <div className="font-semibold text-white">{o.customer?.name}</div>
                  <div className="flex items-center gap-1"><Phone size={13} className="text-pink" /> {o.customer?.phone}</div>
                  {o.mode === "livraison" && o.customer?.address && <div className="flex items-start gap-1"><MapPin size={13} className="text-pink mt-0.5" /> {o.customer.address}</div>}
                  {o.customer?.note && <div className="text-white/50 italic">Note : {o.customer.note}</div>}
                </div>
                <div className="bg-[#0f0f0f] rounded-xl p-3 my-3">
                  {(o.items || []).map((l, i) => <OrderLine key={`${o.id}-${l.name}-${i}`} l={l} />)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-xs">{o.email_sent ? "Email envoyé" : "Email non envoyé"}</span>
                  <span className="font-display text-2xl text-pink">{fmt(o.total)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Commandes;
